import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreIndustriesComponent } from '../explore-more-industries/explore-more-industries.component';
import { trigger, transition, query, style, stagger, animate } from '@angular/animations';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface CaseStudy {
  image: string;
  title: string;
  description: string;
}
@Component({
  selector: 'app-hi-tech',
  imports: [CommonModule, RouterModule, FaqComponent, ExploreMoreIndustriesComponent],
  standalone: true,
  templateUrl: './hi-tech.component.html',
  styleUrl: './hi-tech.component.scss',
  animations: [
    trigger('fadeInUpStagger', [
      transition('* => visible', [
        query('.animate-item', [
          style({ opacity: 0, transform: 'translateY(30px)' }),
          stagger('200ms', [
            animate('800ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class HiTechComponent implements AfterViewInit, OnInit, OnDestroy {
  private jsonLdScript: any;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('impactSection') impactSection!: ElementRef;
  impactSectionVisible = false;
  private observer: IntersectionObserver | null = null;

  caseStudies: CaseStudy[] = [
    {
      image: '../../../../assets/industries/hitech/hitech1.png',
      title: 'Transforming Recruitment Operations with Next-Gen Automation',
      description: 'We modernized our internal hiring process by replacing fragmented, Excel-based workflows with a fully automated, role-based recruitment system. The platform now centralizes candidate data, streamlines approvals, and brings full transparency to every hiring stage. With real-time analytics, historical data retention, and RAG chatbot integration, HR teams can make faster, data-driven decisions, achieving a 70% candidate engagement rate, 45% higher screening efficiency, and 50% fewer manual follow-ups, enabling positions to be closed up to 30% faster.'
    }
    // {
    //   image: '../../../../assets/industries/healthcare/case-study-healthcare.png',
    //   title: 'Automated Billing Solution for Insurance Efficiency',
    //   description: 'Kalpita Technologies developed a scalable automated billing system integrated with Azure cloud functionalities to overcome manual errors, billing inconsistencies, and delayed notifications faced by a top-tier insurance company. The system supports multiple billing cycles, handles cancellations and renewals, and offers a user-friendly interface for real-time billing status management. This solution enhanced 100% accuracy and customer engagement through proactive alerts and transparent billing history, transforming the billing process into an efficient, error-free operation that meets the evolving needs of the insurance industry.'
    // },
    // {
    //   image: '../../../../assets/industries/healthcare/case-study-healthcare.png',
    //   title: 'Unified Customer Engagement Portal',
    //   description: 'Kalpita Technologies partnered with a top-tier insurance provider to modernize their customer engagement ecosystem through a unified digital portal. The platform consolidated multiple service lines into one secure, SOC2 and GDPR compliant environment, enabling seamless client–advisor collaboration, consistent brand communication, and actionable business insights via integrated Power BI dashboards. This transformation enhanced customer retention, improved decision-making, and reinforced the client’s brand identity across all insurance verticals.'
    // }
  ];
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  HiTechFaqs: FaqItem[] = [
    {
      question: 'What makes hi-tech software development different from traditional software development?',
      answer: ` Hi-tech software development leverages cutting-edge technologies—AI/ML, cloud-native 
      architectures, intelligent automation, and real-time analytics—to build solutions that 
      are inherently scalable, adaptive, and future-proof. Unlike traditional development, 
      it emphasizes rapid iteration, API-first design, microservices architecture, and AI-driven 
      decision-making to keep pace with the fast-evolving tech landscape.`
    },
    {
      question: 'Can you integrate AI solutions into our existing tech stack without disrupting operations?',
      answer: `Absolutely. We specialize in phased, non-disruptive integration strategies. 
      Our approach includes API-first design for seamless connectivity, parallel running of 
      old and new systems during transition, incremental migration with rollback capabilities, 
      and comprehensive testing at every stage. We ensure business continuity while modernizing 
      your infrastructure.`
    },
    {
      question: 'How quickly can we see ROI from AI-driven software solutions?',
      answer: `Most clients see a measurable impact within 3-6 months. Quick wins include 
      automated workflows reducing manual effort by 50-70%, AI-powered analytics delivering 
      actionable insights within weeks, and cloud migration cutting infrastructure costs by 30-40%.`
    },
    {
      question: 'How do you measure success for hi-tech software projects?',
      answer: `We establish clear, measurable KPIs from day one: deployment frequency and lead time, 
      system uptime and performance metrics, user adoption and satisfaction scores, cost savings and 
      efficiency gains, revenue impact and business growth metrics, and technical debt reduction. 
      We provide real-time dashboards and monthly reports, so you always know exactly how the 
      solution is performing.`
    }
  ];

  currentSlide = 0;
  isDragging = false;
  startPosition = 0;
  currentPosition = 0;
  transformValue = 0;
  private autoSlideInterval: any;
  private readonly SLIDE_DURATION = 5000;

  constructor(private router: Router, private modalService: NgbModal,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.stopAutoSlide();
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    // 4. Set up Intersection Observer
    const options = {
      root: null,
      threshold: 0.2,
      rootMargin: '0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.impactSectionVisible = true;
          this.observer?.disconnect();
        }
      });
    }, options);

    if (this.impactSection) {
      this.observer.observe(this.impactSection.nativeElement);
    }

    // 5. Initialize 3D Tilt Effect
    this.setupTiltEffect();
  }

  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.solution-image-card, .impact-image-wrapper, .case-study-image-wrapper');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;
        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  scheduleDemo(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  startAutoSlide(): void {
    this.stopAutoSlide(); // Ensure no duplicate intervals

    // MODIFIED: Only start auto-slide if there is more than 1 slide
    if (this.caseStudies.length > 1) {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, this.SLIDE_DURATION);
    }
  }

  stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }

  nextSlide(): void {
    if (this.currentSlide < this.caseStudies.length - 1) {
      this.currentSlide++;
    } else {
      this.currentSlide = 0;
    }
  }

  goToSlide(index: number): void {
    if (this.caseStudies.length <= 1) return;
    this.currentSlide = index;
    this.stopAutoSlide();
    this.startAutoSlide();
  }

  onMouseEnter(): void {
    this.stopAutoSlide();
  }

  onMouseLeave(): void {
    this.startAutoSlide();
  }

  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.startPosition = event.clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  onTouchStart(event: TouchEvent): void {
    this.isDragging = true;
    this.startPosition = event.touches[0].clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.touches[0].clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  redirectToAbout(): void {
    this.router.navigate(['/about-us']);
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.startAutoSlide();
  }
  injectSchemaMarkup(): void {
    const hitechSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/industries/hitech#webpage",
          "url": "https://www.kalpitatechnologies.com/industries/hitech",
          "name": "Hi-Tech Software Development | AI-Driven Enterprise Solutions | Kalpita Technologies",
          "description": "Future-ready hi-tech software development by Kalpita Technologies. AI-powered, cloud-native, and enterprise-grade solutions for rapid innovation.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/industries/hitech#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Industries", "item": "https://www.kalpitatechnologies.com/industries" },
            { "@type": "ListItem", "position": 3, "name": "Hi-Tech", "item": "https://www.kalpitatechnologies.com/industries/hitech" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/industries/hitech#service",
          "name": "Hi-Tech Software Development Solutions",
          "serviceType": "Hi-Tech Enterprise Software Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "Future-ready hi-tech software development delivering AI-driven, cloud-native, and enterprise-grade solutions for technology enterprises."
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/industries/hitech#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What makes hi-tech software development different from traditional software development?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Hi-tech software development leverages cutting-edge technologies—AI/ML, cloud-native architectures, intelligent automation, and real-time analytics."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(hitechSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  onCardClick(index: number): void {
    console.log('Clicked card', index);
  }

  getStarArray(rating: number): boolean[] {
    return Array(5).fill(false).map((_, index) => index < rating);
  }

  testimonials(): void {
    this.router.navigate(['/'], { fragment: 'testimonials' });
  }
}
